// Win/loss ratio donut chart. Mirrors TradingView's "Win/loss ratio" panel — two arcs
// (green wins / red losses) with the total trade count centred. Break-even slice supported
// for completeness even though our engine collapses zero-return trades into "losses".

import type { BacktestResponseDto } from "../../api/types";

interface Props {
  result: BacktestResponseDto;
  size?: number;
}

interface Counts {
  wins: number;
  losses: number;
  breakEven: number;
}

function pickCounts(result: BacktestResponseDto): Counts {
  const fromReturns = (returns: number[]): Counts => {
    // Match the engine + the scan-fallback path: a win is strictly-positive; a zero-return
    // trade counts as a loss (not break-even). Keeps the donut consistent regardless of
    // whether the primary block carries returns[] (netting/merged) or wins/losses (scan).
    let wins = 0, losses = 0;
    for (const r of returns) {
      if (r > 0) wins++;
      else losses++;
    }
    return { wins, losses, breakEven: 0 };
  };
  if (result.strategy) return fromReturns(result.strategy.returns);
  if (result.merged) return fromReturns(result.merged.returns);
  let wins = 0, losses = 0, breakEven = 0;
  for (const s of [result.long, result.short]) {
    if (!s) continue;
    wins += s.wins;
    losses += s.losses;
  }
  return { wins, losses, breakEven };
}

export function WinLossDonut({ result, size = 180 }: Props) {
  const c = pickCounts(result);
  const total = c.wins + c.losses + c.breakEven;
  if (total === 0) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 4, fontSize: 12, color: "#8b949e" }}>
        No trades to count.
      </div>
    );
  }

  // SVG donut. Two arcs (or three) drawn via stroke-dasharray on a circle.
  const r = (size - 30) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  const winFrac = c.wins / total;
  const lossFrac = c.losses / total;
  const beFrac = c.breakEven / total;

  // We compose three arcs by rotating the start position.
  const winLen = winFrac * circumference;
  const lossLen = lossFrac * circumference;
  const beLen = beFrac * circumference;

  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Win/loss ratio</div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background track */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#161b22" strokeWidth={14} />
          {/* Wins arc */}
          <circle
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke="#3fb950"
            strokeWidth={14}
            strokeDasharray={`${winLen} ${circumference - winLen}`}
            strokeDashoffset={0}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          {/* Losses arc */}
          <circle
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke="#f85149"
            strokeWidth={14}
            strokeDasharray={`${lossLen} ${circumference - lossLen}`}
            strokeDashoffset={-winLen}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          {/* Break-even arc */}
          {c.breakEven > 0 && (
            <circle
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke="#d29922"
              strokeWidth={14}
              strokeDasharray={`${beLen} ${circumference - beLen}`}
              strokeDashoffset={-(winLen + lossLen)}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          )}
          {/* Centre label */}
          <text x={cx} y={cy - 4} textAnchor="middle" fill="#c9d1d9" fontSize={22} fontWeight={600}>
            {total.toLocaleString()}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" fill="#8b949e" fontSize={10}>
            Total trades
          </text>
        </svg>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12 }}>
          <Legend color="#3fb950" label="Wins"    count={c.wins}    pct={winFrac * 100} />
          <Legend color="#f85149" label="Losses"  count={c.losses}  pct={lossFrac * 100} />
          <Legend color="#d29922" label="Break even" count={c.breakEven} pct={beFrac * 100} />
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label, count, pct }: { color: string; label: string; count: number; pct: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 10, height: 10, borderRadius: 10, background: color, display: "inline-block" }} />
      <span style={{ color: "#8b949e", minWidth: 80 }}>{label}</span>
      <span style={{ color: "#c9d1d9", fontFamily: "monospace", minWidth: 50, textAlign: "right" }}>{count.toLocaleString()} trades</span>
      <span style={{ color: "#8b949e", fontFamily: "monospace", minWidth: 50, textAlign: "right" }}>{pct.toFixed(2)}%</span>
    </div>
  );
}
